/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { AxiosResponse, AxiosInstance } from 'axios';
import { CallClosedReason, CallClosedReasonV1 } from '../../types/call';
import { CommonAPI } from '../common/responses';
import { baseUrl, NotaryAPI } from './types';

export const callsClient = (request: AxiosInstance) => ({
  async fetchCallUserData(callId: string): Promise<CommonAPI.UserDataResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}/user-data`)).data;
  },

  async admitBrowser(callId: string, sessionId: string): Promise<void> {
    await request.post(`${baseUrl}/sessions/${sessionId}/add-to-call`, { callId });
  },

  async removeBrowser(sessionId: string): Promise<void> {
    await request.delete(`${baseUrl}/sessions/${sessionId}`);
  },

  async getParticipantSessionDetailsForReview(
    participantSessionId: string,
  ): Promise<NotaryAPI.ParticipantSessionForReviewResponse> {
    return (await request.get(`${baseUrl}/participant-sessions/${participantSessionId}/review`)).data;
  },

  async approveParticipantSession(participantSessionId: string): Promise<void> {
    await request.post(`${baseUrl}/participant-sessions/${participantSessionId}/approve`);
  },

  async fetchCallDetailsForRoom(roomId: string): Promise<NotaryAPI.CallDetailsResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/call`)).data;
  },

  async fetchCall(callId: string): Promise<NotaryAPI.CallDetailsResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}`)).data;
  },

  async fetchCallEvents(callId: string): Promise<NotaryAPI.CallEventsResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}/events`)).data;
  },

  async recordConsent(callId: string): Promise<AxiosResponse> {
    return request.post(`${baseUrl}/calls/${callId}/consent`);
  },

  async startSigning(callId: string): Promise<void> {
    await request.post(`${baseUrl}/calls/${callId}/start-signing`);
  },

  async pauseSigning(callId: string): Promise<void> {
    await request.post(`${baseUrl}/calls/${callId}/pause-signing`);
  },

  async completeSigningForCall(callId: string): Promise<void> {
    return await request.post(`${baseUrl}/calls/${callId}/complete`);
  },

  async startCall(callId: string): Promise<NotaryAPI.CallDetailsResponse> {
    return (await request.post(`${baseUrl}/calls/${callId}/start`)).data;
  },

  async startVideoRecording(callId: string): Promise<NotaryAPI.CallDetailsResponse> {
    return (await request.post(`${baseUrl}/calls/${callId}/start-video`)).data;
  },

  async stopCall(
    callId: string,
    reason: CallClosedReason | CallClosedReasonV1,
    needReschedule?: boolean,
    comments?: string,
  ) {
    return (await request.post(`${baseUrl}/calls/${callId}/stop`, { reason, needReschedule, comments })).data;
  },

  async prepareSigningEnvelope(callId: string): Promise<void> {
    await request.post(`${baseUrl}/calls/${callId}/prepare-signing-envelope`);
  },

  async createNotaryFeedback(callId: string, body: NotaryAPI.CreateFeedbackRequestBody) {
    return await request.post(`${baseUrl}/calls/${callId}/create-feedback`, { ...body });
  },

  async saveAdditionVideoRoomId(
    callId: string,
    body: NotaryAPI.SaveAdditionalVideoRoomIdRequestBody,
  ): Promise<NotaryAPI.SaveAdditionVideoRoomIdResponse> {
    return await request.post(`${baseUrl}/calls/${callId}/update-video-session-id`, { ...body });
  },
});
